import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { Check, ChevronRight, ExternalLink, Loader2, Search, Filter as FilterIcon } from 'lucide-react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';
import { useBreadcrumb, useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import { listQueue, getQueueCounts, getQueueItem, gradeQueueItem, gradeQueueItems } from 'features/evals3/api/evals3Api';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import TraceDetailPage from 'features/agent-analytics/pages/TraceDetailPage';
import ConversationDetailDrawer from 'features/agent-analytics/components/ConversationDetailDrawer';
import AuditDisagreementsView from 'features/evals3/pages/AuditDisagreementsView';
import BrandLoader, { PageLoader } from 'shared/components/ui/BrandLoader';
import SidePanel from 'shared/components/ui/SidePanel';
import TableFooter from 'shared/components/tables/TableFooter';
import { activityToolbarButtonClass, tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass, activitySearchWrapperClass, activitySearchInputClass } from 'features/activity/styles/dataTableStyles';

/**
 * The grading queue for the new evals' human steps (TRO-428).
 *
 * A reviewer works a queue, not one item. So the queue is a list of one-line
 * rows — what was asked, what the agent answered, how long it has waited —
 * and every row carries the step's own options as buttons. Most items can be
 * answered from the list without opening anything, which is the difference
 * between grading two hundred traces and giving up at thirty.
 *
 * A step decides one thing, and there are three shapes that thing takes —
 * pick one of these, yes or no, or a rating in a range. The control follows
 * the shape: buttons, a pair of thumbs, or a scale with the step's own
 * thresholds drawn on it, so a reviewer can see that 3 still fails before
 * they land on 3.
 *
 * Three ways to answer, all the same grade underneath:
 *
 *   in the row     one click, for the obvious ones
 *   in a selection tick several (or filter, then tick them all) and answer
 *                  them together — an item whose pinned version does not
 *                  declare that answer is skipped and named, never coerced
 *   in the panel   open a row beside the list — the page shrinks rather than
 *                  being covered — for the whole trace, what the cheap checks
 *                  already decided, and where each answer leads; keys 1–9
 *                  pick one and the next item opens
 *
 * Grading resumes the eval on the worker under the version it started with.
 * An item someone else graded first says so and moves on; it is not an error.
 */

const th = tableHeaderCellClass;
const td = tableDataCellClass;
/** How much of the queue one page holds; the count beside it is the whole. */
const PAGE_LIMIT = 50;

function ago(iso) {
  if (!iso) return '';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

function fmt(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(3)));
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function Block({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{label}</div>
      {children}
    </div>
  );
}

const pre = 'max-h-56 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] p-2 text-[12px] text-[color:var(--text-primary)]';

/**
 * What was said, in prose — with the payload it arrived in one click away.
 * A reviewer reads the sentence; the envelope only matters when the envelope
 * is what they doubt, so it is offered rather than shown.
 */
function Said({ text, raw }) {
  const [showRaw, setShowRaw] = useState(false);
  const hasRaw = Boolean(raw) && raw !== text;
  return (
    <>
      <div className={pre}>{(showRaw ? raw : text) || '—'}</div>
      {hasRaw && (
        <button type="button" onClick={() => setShowRaw((v) => !v)}
          className="mt-1 text-[10.5px] text-[color:var(--text-muted)] underline-offset-2 hover:text-[color:var(--text-secondary)] hover:underline">
          {showRaw ? 'Read it as text' : 'Show what was sent'}
        </button>
      )}
    </>
  );
}

/**
 * An option's tone, so a yes reads as a yes at a glance. Steps name their
 * options themselves, so this recognises the usual words and leaves anything
 * else plain rather than guessing a meaning for it. The word is always shown:
 * a thumb on its own would make the reviewer learn the step's wording twice.
 */
const YES = /^(y|yes|pass|passed|good|correct|accept|accepted|acceptable|approve|approved|ok|okay|true|relevant|grounded|helpful|safe|complete|resolved)$/i;
const NO = /^(n|no|fail|failed|bad|wrong|incorrect|not[_\- ]?acceptable|unacceptable|reject|rejected|false|irrelevant|ungrounded|unhelpful|unsafe|harmful|incomplete|unresolved)$/i;
const toneOf = (v) => (YES.test(String(v)) ? 'yes' : NO.test(String(v)) ? 'no' : 'other');
const TONE_HOVER = {
  yes: 'hover:border-[color:var(--feedback-success)] hover:text-success',
  no: 'hover:border-[color:var(--feedback-error)] hover:text-danger',
  other: 'hover:border-[color:var(--border-strong)] hover:text-[color:var(--text-primary)]',
};
function ToneIcon({ tone, className = 'h-3 w-3' }) {
  if (tone === 'yes') return <ThumbIcon direction="up" size={12} className={className} />;
  if (tone === 'no') return <ThumbIcon direction="down" size={12} className={className} />;
  return null;
}

/**
 * A rating's scale, as the values a reviewer can actually land on. A step
 * that does not declare one has a continuous scale, and gets a slider
 * instead of buttons.
 */
const MAX_SCALE_BUTTONS = 7;
function scaleValues(o) {
  if (!o || o.type !== 'number' || !o.step) return [];
  const n = Math.round((o.max - o.min) / o.step) + 1;
  if (!Number.isFinite(n) || n < 2 || n > MAX_SCALE_BUTTONS) return [];
  return Array.from({ length: n }, (_, i) => Number((o.min + i * o.step).toPrecision(12)));
}
const bandAt = (bands, v) => (bands || []).find((b) => v >= b.from_value && v <= b.to_value) || null;
/** The word shown for a value: an enum says itself, a boolean says yes or no. */
const wordFor = (options, v) => (options?.type === 'boolean' ? (v === 'true' ? 'yes' : 'no') : String(v));

const OPTION_BTN = 'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md border border-[color:var(--border-default)] px-2 text-[11.5px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover disabled:opacity-40';

/** Pick one of these, or yes/no: one button each. */
function OptionButtons({ options, routes = [], busy, onGrade, labelFor }) {
  return (
    <span className="flex flex-wrap items-center justify-end gap-1">
      {options.values.map((v) => {
        const word = wordFor(options, v);
        const tone = toneOf(word);
        const route = routes.find((r) => r.value === v);
        return (
          <button key={v} type="button" disabled={Boolean(busy)} onClick={(e) => { e.stopPropagation(); onGrade(v); }}
            aria-label={labelFor(word)} title={route ? `${word} — then ${route.label}` : word}
            className={`${OPTION_BTN} ${TONE_HOVER[tone]}`}>
            {busy === v ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <ToneIcon tone={tone} />}
            {word}
          </button>
        );
      })}
    </span>
  );
}

/** Too many options for a row of buttons: a list that grades on choosing. */
function OptionSelect({ options, routes = [], busy, onGrade, name }) {
  return (
    <span className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()} role="presentation">
      {busy ? <Loader2 className="h-3 w-3 animate-spin text-[color:var(--text-muted)]" aria-hidden /> : null}
      <select value="" disabled={Boolean(busy)} aria-label={name} onChange={(e) => e.target.value && onGrade(e.target.value)}
        className="h-6 max-w-[150px] rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-1 text-[11.5px] text-[color:var(--text-secondary)] outline-none focus:border-[color:var(--border-strong)] disabled:opacity-40">
        <option value="">Answer…</option>
        {options.values.map((v) => {
          const route = routes.find((r) => r.value === v);
          return <option key={v} value={v}>{wordFor(options, v)}{route ? ` → ${route.label}` : ''}</option>;
        })}
      </select>
    </span>
  );
}

/** A short rating scale: one button per point, the way a 1–5 is answered. */
function ScaleButtons({ options, bands, busy, onGrade, labelFor }) {
  const values = scaleValues(options);
  return (
    <span className="flex items-center justify-end gap-0.5">
      {values.map((v) => {
        const band = bandAt(bands, v);
        return (
          <button key={v} type="button" disabled={Boolean(busy)} onClick={(e) => { e.stopPropagation(); onGrade(String(v)); }}
            aria-label={labelFor(v)} title={band ? `${v} — then ${band.label}` : String(v)}
            className={`${OPTION_BTN} min-w-[26px] justify-center px-1.5 tabular-nums ${TONE_HOVER.other}`}>
            {busy === String(v) ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : v}
          </button>
        );
      })}
    </span>
  );
}

/**
 * A rating on a scale too long to press: drag, then commit. The bands are
 * drawn under the track, so where the line falls is visible before the
 * reviewer lets go of it.
 */
function ScaleSlider({ options, bands, busy, onGrade, labelFor, name, submitLabel = 'Grade', showBands = false }) {
  const { min, max } = options;
  const step = options.step || Number(((max - min) / 100).toPrecision(6));
  const [value, setValue] = useState(() => Number(((min + max) / 2).toPrecision(12)));
  const band = bandAt(bands, value);
  const span = max - min || 1;
  return (
    <span className="flex w-full flex-col gap-1" onClick={(e) => e.stopPropagation()} role="presentation">
      <span className="flex items-center gap-2">
        <input type="range" min={min} max={max} step={step} value={value} aria-label={name} disabled={Boolean(busy)}
          onChange={(e) => setValue(Number(e.target.value))} className="h-1 min-w-0 flex-1 accent-[color:var(--trodo-primary)]" />
        <span className="w-8 shrink-0 text-right text-[12px] font-semibold tabular-nums text-[color:var(--text-primary)]">{value}</span>
        <button type="button" disabled={Boolean(busy)} onClick={() => onGrade(String(value))} aria-label={labelFor(value)}
          className={`${OPTION_BTN} ${TONE_HOVER.other}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Check className="h-3 w-3" aria-hidden />}
          {submitLabel}
        </button>
      </span>
      {showBands && bands?.length > 1 && (
        <span className="flex h-4 w-full overflow-hidden rounded-sm" aria-hidden>
          {bands.map((b) => (
            <span key={b.from_value} title={`${b.from_value}–${b.to_value}: ${b.label}`}
              style={{ width: `${((b.to_value - b.from_value + step) / (span + step)) * 100}%` }}
              className="flex items-center justify-center truncate border-r border-[color:var(--surface-base)] bg-[color:var(--surface-sunken)] px-1 text-[9.5px] text-[color:var(--text-muted)] last:border-r-0">
              {b.label}
            </span>
          ))}
        </span>
      )}
      {band && <span className="text-[10.5px] text-[color:var(--text-muted)]">then {band.label}</span>}
    </span>
  );
}

/**
 * A rating, with room to explain itself: the ends named if the step named
 * them, every point that routes differently marked, and what the current
 * value would do said in words.
 */
function PanelScale({ options, bands, busy, onGrade }) {
  const points = scaleValues(options);
  const { min, max, labels } = options;
  return (
    <div className="flex flex-col gap-2">
      {(labels?.min || labels?.max) && (
        <div className="flex justify-between text-[11px] text-[color:var(--text-muted)]">
          <span>{min}{labels?.min ? ` · ${labels.min}` : ''}</span>
          <span>{max}{labels?.max ? ` · ${labels.max}` : ''}</span>
        </div>
      )}
      {points.length ? (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Rating">
          {points.map((v) => {
            const band = bandAt(bands, v);
            return (
              <button key={v} type="button" disabled={Boolean(busy)} onClick={() => onGrade(String(v))} aria-label={`Grade ${v}`}
                className={`flex min-w-[76px] flex-col items-center rounded-lg border border-[color:var(--border-default)] px-3 py-2 hover:bg-surface-hover disabled:opacity-50 ${TONE_HOVER.other}`}>
                <span className="flex items-center gap-1.5 text-[15px] font-semibold tabular-nums text-[color:var(--text-primary)]">
                  {busy === String(v) ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : v}
                </span>
                {band && <span className="text-[10.5px] text-[color:var(--text-muted)]">then {band.label}</span>}
              </button>
            );
          })}
        </div>
      ) : (
        <ScaleSlider options={options} bands={bands} busy={busy} onGrade={onGrade} name="Rating" labelFor={(v) => `Grade ${v}`} submitLabel="Grade" showBands />
      )}
    </div>
  );
}

/**
 * The control this step's answer deserves, wherever it is being answered.
 * `place` only changes how much room it may take, never what it asks for.
 */
function AnswerControl({ options, routes, bands, busy, onGrade, labelFor, name, place = 'row' }) {
  if (!options?.field) {
    return <span className="text-[11px] text-[color:var(--text-muted)]" title="This step declares nothing for a person to decide in the version it ran under.">Open to grade</span>;
  }
  if (options.type === 'number') {
    if (scaleValues(options).length) return <ScaleButtons options={options} bands={bands} busy={busy} onGrade={onGrade} labelFor={labelFor} />;
    return <ScaleSlider options={options} bands={bands} busy={busy} onGrade={onGrade} labelFor={labelFor} name={name} showBands={place !== 'row'} />;
  }
  if (options.values.length > 4 && place === 'row') {
    return <OptionSelect options={options} routes={routes} busy={busy} onGrade={onGrade} name={name} />;
  }
  return <OptionButtons options={options} routes={routes} busy={busy} onGrade={onGrade} labelFor={labelFor} />;
}

/**
 * `embedded`: one eval's queue as a tab of its results page — no page
 * header and no eval switcher (the eval is fixed); `onCounted(n)` reports
 * how many are waiting, for the tab's badge.
 */
const unitIdOf = (it) => {
  const id = it.target_kind === 'span' ? it.span_id : it.target_kind === 'conversation' ? it.conversation_id : it.run_id;
  return id == null ? null : String(id);
};
/**
 * What kind of answer an item takes: the same kind means the same criteria.
 * Items can be answered together only when this matches.
 */
const gradeKind = (o) => {
  if (!o || !o.field) return null;
  if (o.type === 'number') return `number:${o.min}-${o.max}:${o.step ?? ''}`;
  return `${o.type}:${[...(o.values || [])].sort().join('|')}`;
};
const kindLabel = (o) => {
  if (!o || !o.field) return 'no answer';
  if (o.type === 'number') return `${o.min}\u2013${o.max} scale`;
  if (o.type === 'boolean') return 'yes / no';
  return (o.values || []).join(' / ');
};
const shortId = (id) => (id && id.length > 12 ? `${id.slice(0, 8)}…` : id);

export default function Eval3QueuePage({ teamId, lineageId = null, onBack, onLineage, embedded = false, onCounted, through = [] }) {
  const throughKey = through.join(',');
  useBreadcrumb(embedded ? undefined : []);
  useHideGlobalTopBar(!embedded);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ counts: [], total: 0, audit_counts: [], audit_total: 0 });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);        // the row whose panel is open, if any
  const [detail, setDetail] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [answerType, setAnswerType] = useState('');   // a gradeKind, or '' for any
  const [filterOpen, setFilterOpen] = useState(false);
  const [grading, setGrading] = useState(null);      // { id, choice } | { bulk: true, choice }
  const [notice, setNotice] = useState(null);
  const [done, setDone] = useState(0);
  const [overlay, setOverlay] = useState(null);
  const [mode, setMode] = useState('grade');   // grade: a graph's human steps · audit: disagreements to rule on
  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => { if (embedded) onCounted?.(items.length); }, [embedded, items.length, onCounted]);

  const refresh = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const list = await listQueue(teamId, { lineageId: lineageId || undefined, limit: PAGE_LIMIT, through: throughKey ? throughKey.split(',') : [] });
      setItems(list);
      setError(null);
      setPicked((cur) => new Set([...cur].filter((id) => list.some((i) => i.id === id))));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [teamId, lineageId, throughKey]);
  useEffect(() => { refresh(); }, [refresh]);

  // The totals and the eval picker come on their own: when the list is slow
  // or fails, the picker still has the evals in it.
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    Promise.resolve(getQueueCounts(teamId)).then((c) => { if (!cancelled) setCounts(c); }).catch(() => {});
    return () => { cancelled = true; };
  }, [teamId]);

  /**
   * The rest of the queue.
   *
   * The list asks for one page and stopped there, so on a queue of 1,006 the
   * other 806 could not be reached at all — not to read, and not to filter,
   * since the filter only sees what has been fetched. The endpoint has taken
   * an offset all along; nothing was ever sent.
   *
   * Appending, not replacing: a reviewer part-way down a list they have been
   * grading should not have it move under them.
   */
  const loadMore = useCallback(async () => {
    if (!teamId || loadingMore) return;
    setLoadingMore(true);
    try {
      const more = await listQueue(teamId, {
        lineageId: lineageId || undefined,
        limit: PAGE_LIMIT,
        offset: items.length,
        through: throughKey ? throughKey.split(',') : [],
      });
      // Grading between the two calls shifts the window, so an id already
      // held would arrive twice; the page is the union, in order.
      setItems((cur) => {
        const seen = new Set(cur.map((i) => i.id));
        return [...cur, ...more.filter((i) => !seen.has(i.id))];
      });
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setLoadingMore(false); }
  }, [teamId, lineageId, throughKey, items.length, loadingMore]);

  useEffect(() => {
    if (!openId) { setDetail(null); return undefined; }
    let cancelled = false;
    setDetail(null);
    getQueueItem(teamId, openId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setDetail({ error: e?.response?.data?.error || e.message }); });
    return () => { cancelled = true; };
  }, [teamId, openId]);

  // What the filter box leaves: matched on what a reviewer can actually see.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => (!answerType || gradeKind(it.options) === answerType)
      && (!q || `${it.ask || ''} ${it.answer || ''} ${it.lineage_name || ''}`.toLowerCase().includes(q)));
  }, [items, query, answerType]);
  const answerTypes = useMemo(() => {
    const seen = new Map();
    for (const it of items) { const k = gradeKind(it.options); if (k && !seen.has(k)) seen.set(k, kindLabel(it.options)); }
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [items]);
  // Items answered together share their grading criteria: once one is ticked,
  // only items of the same kind can be.
  const kindById = useMemo(() => new Map(items.map((i) => [i.id, gradeKind(i.options)])), [items]);
  const pickedKind = picked.size ? kindById.get(picked.values().next().value) ?? null : null;
  const canPick = (it) => {
    const k = gradeKind(it.options);
    return Boolean(k) && (!picked.size || k === pickedKind);
  };
  const selectable = useMemo(() => {
    const first = pickedKind || gradeKind(visible.find((i) => gradeKind(i.options))?.options);
    return first ? visible.filter((i) => gradeKind(i.options) === first) : [];
  }, [visible, pickedKind]);
  const allPicked = selectable.length > 0 && selectable.every((i) => picked.has(i.id));
  // How many are actually waiting: the totals, which count the whole queue,
  // not the page of it this list is holding.
  const waiting = lineageId
    ? (counts.counts.find((c) => c.lineage_id === lineageId)?.open_items ?? items.length)
    : (embedded ? items.length : counts.total);
  // A column wide enough for what these steps ask: thumbs need little, a
  // slider needs room to be draggable, and a long option list sits between.
  const answerColumnWidth = useMemo(() => {
    let w = 152;
    for (const it of visible) {
      const o = it.options;
      if (!o) continue;
      if (o.type === 'number' && !scaleValues(o).length) w = Math.max(w, 260);
      else if (o.type === 'number') w = Math.max(w, 30 * scaleValues(o).length + 24);
      else if (o.values?.length > 4) w = Math.max(w, 170);
      else w = Math.max(w, 44 * (o.values?.length || 2) + 16);
    }
    return Math.min(w, 300);
  }, [visible]);

  /**
   * What a selection can be answered with: the answer every chosen item can
   * take, so a control on the bar is never one some of them would refuse.
   * Lists intersect; ratings need the same shape of scale, and the bar offers
   * the overlap of their ranges.
   */
  const sharedAnswer = useMemo(() => {
    const chosen = items.filter((i) => picked.has(i.id)).map((i) => i.options).filter((o) => o && o.field);
    if (!chosen.length || chosen.length !== picked.size) return null;
    const type = chosen[0].type;
    if (!chosen.every((o) => o.type === type)) return null;
    if (type === 'number') {
      const min = Math.max(...chosen.map((o) => o.min));
      const max = Math.min(...chosen.map((o) => o.max));
      if (min > max) return null;
      const step = chosen.every((o) => o.step === chosen[0].step) ? chosen[0].step : null;
      return { options: { field: 'rating', type: 'number', min, max, step, values: [] }, bands: [] };
    }
    const values = chosen[0].values.filter((v) => chosen.every((o) => o.values.includes(v)));
    if (!values.length) return null;
    return { options: { field: 'choice', type, values }, bands: [] };
  }, [items, picked]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  /**
   * Items that have left the queue: gone from the list, the selection and the
   * totals. `advance` is the row the panel was open on — the panel moves to
   * what took its place, so grading from the panel keeps its rhythm.
   */
  const leave = useCallback((ids, { advance = null } = {}) => {
    const gone = new Set(ids);
    const cur = itemsRef.current;
    const at = advance ? cur.findIndex((x) => x.id === advance) : -1;
    const next = cur.filter((x) => !gone.has(x.id));
    const byLineage = new Map();
    for (const x of cur) if (gone.has(x.id)) byLineage.set(x.lineage_id, (byLineage.get(x.lineage_id) || 0) + 1);

    setItems(next);
    if (advance) setOpenId(next.length ? (next[Math.min(Math.max(at, 0), next.length - 1)]?.id ?? null) : null);
    setCounts((c) => ({
      ...c,
      total: Math.max(0, c.total - gone.size),
      counts: c.counts.map((x) => (byLineage.has(x.lineage_id) ? { ...x, open_items: x.open_items - byLineage.get(x.lineage_id) } : x)).filter((x) => x.open_items > 0),
    }));
    setPicked((p) => new Set([...p].filter((id) => !gone.has(id))));
  }, []);

  const grade = useCallback(async (id, choice, { fromPanel = false } = {}) => {
    if (grading) return;
    setGrading({ id, choice });
    setNotice(null);
    try {
      const r = await gradeQueueItem(teamId, id, choice);
      if (r.already_graded) {
        setNotice({ id, text: `Someone already graded this one: “${r.item?.graded_choice}”. Nothing was changed.` });
      } else {
        setDone((n) => n + 1);
        leave([id], { advance: fromPanel ? id : null });
      }
    } catch (e) {
      setNotice({ id, text: e?.response?.data?.message || e?.response?.data?.error || e.message, error: true });
    } finally { setGrading(null); }
  }, [grading, teamId, leave]);

  const gradePicked = useCallback(async (choice) => {
    const ids = [...picked];
    if (!ids.length || grading) return;
    setGrading({ bulk: true, choice });
    setNotice(null);
    try {
      const r = await gradeQueueItems(teamId, ids, choice);
      const left = [...r.graded, ...r.already_graded];
      setDone((n) => n + r.graded.length);
      if (left.length) leave(left, { advance: left.includes(openId) ? openId : null });
      const parts = [`${r.graded.length} graded “${choice}”`];
      if (r.already_graded.length) parts.push(`${r.already_graded.length} someone else had already done`);
      if (r.skipped.length) parts.push(`${r.skipped.length} left alone: their step does not offer “${choice}”`);
      setNotice({ bulk: true, text: `${parts.join(' · ')}.`, error: r.graded.length === 0 });
    } catch (e) {
      setNotice({ bulk: true, text: e?.response?.data?.message || e?.response?.data?.error || e.message, error: true });
    } finally { setGrading(null); }
  }, [picked, grading, teamId, leave, openId]);

  const gradeRef = useRef(grade);
  gradeRef.current = grade;
  const panelRef = useRef({ id: null, values: [] });
  panelRef.current = { id: detail?.item?.id ?? null, values: detail?.options?.values || [], options: detail?.options ?? null };
  useEffect(() => {
    const onKey = (e) => {
      if (modeRef.current !== 'grade') return;
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const { id, values, options } = panelRef.current;
      if (!id) return;
      const n = Number(e.key);
      if (!Number.isInteger(n)) return;
      // A rating is answered by its own number, not by a position in a list:
      // on a 1-5 scale, 4 means 4.
      if (options?.type === 'number') {
        if (scaleValues(options).includes(n)) gradeRef.current(id, String(n), { fromPanel: true });
        return;
      }
      if (n >= 1 && n <= 9 && values[n - 1]) gradeRef.current(id, values[n - 1], { fromPanel: true });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const target = detail?.target;
  const trace = detail?.trace;
  const openFull = () => {
    if (!target) return;
    if (target.kind === 'run') setOverlay({ kind: 'run', id: target.run_id });
    else if (target.kind === 'span') setOverlay({ kind: 'span', id: target.span_id });
    else setOverlay({ kind: 'conversation', id: target.conversation_id });
  };
  const pickerCounts = mode === 'audit' ? (counts.audit_counts || []) : counts.counts;
  const pickerTotal = mode === 'audit' ? (counts.audit_total || 0) : counts.total;
  const filterName = lineageId ? (pickerCounts.find((c) => c.lineage_id === lineageId)?.name || counts.counts.find((c) => c.lineage_id === lineageId)?.name || items[0]?.lineage_name || 'this eval') : null;
  const showEvalColumn = !embedded && !lineageId;
  const Root = embedded ? 'div' : 'main';
  const toolbar = (
<div className={`flex flex-wrap items-center gap-2 ${embedded ? "min-w-0 flex-1" : ""}`}>
          <label className={`${activitySearchWrapperClass} w-56`}>
            <Search className="h-3.5 w-3.5 flex-none text-[color:var(--text-muted)]" aria-hidden />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter what is waiting" aria-label="Filter what is waiting"
              className={activitySearchInputClass} />
          </label>
          <div className="relative">
            <button type="button" onClick={() => setFilterOpen((v) => !v)} aria-haspopup="dialog" aria-expanded={filterOpen} className={activityToolbarButtonClass}>
              <FilterIcon size={13} aria-hidden /> Filter
              {answerType && <span className="rounded-full bg-brand-soft px-1.5 text-[10.5px] font-semibold text-brand">1</span>}
            </button>
            {filterOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)} role="presentation" />
                <div role="dialog" aria-label="Filter the queue" className="absolute left-0 top-full z-50 mt-1 flex w-64 flex-col gap-1 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-3 shadow-token-lg">
                  <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Answer type</span>
                  <BrandDropdown compact ariaLabel="Answer type" value={answerType} onChange={setAnswerType}
                    options={[{ value: '', label: 'Any' }, ...answerTypes]} />
                  {answerType && <button type="button" onClick={() => setAnswerType('')} className="mt-1 self-start text-[11.5px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Clear</button>}
                </div>
              </>
            )}
          </div>
          {(query || answerType) && <button type="button" onClick={() => { setQuery(''); setAnswerType(''); }} className="h-8 rounded-md px-2 text-[11.5px] text-[color:var(--text-muted)] hover:bg-surface-hover">Clear filter</button>}
          <div className="flex-1" />
          {/* The truth, not the page. The list is capped at PAGE_LIMIT, and a
              queue of 886 that says "200 waiting" tells a reviewer they are
              nearly done when they have not started.

              The filter is the same promise in the other direction: it runs
              over the rows already fetched, so it can only ever say how many
              of THOSE match. "30 of 1,070 match" claimed it had read a queue
              it never saw. It counts against what it actually searched. */}
          <span className="text-[11.5px] tabular-nums text-[color:var(--text-muted)]" role="status">
            {(query || answerType)
              ? `${visible.length.toLocaleString()} of ${items.length.toLocaleString()} ${items.length < waiting ? 'loaded ' : ''}match`
              : `${waiting.toLocaleString()} waiting`}
            {!(query || answerType) && items.length < waiting ? `, ${items.length.toLocaleString()} loaded` : ''}
            {(query || answerType) && items.length < waiting ? ` · ${waiting.toLocaleString()} waiting in all` : ''}
            {done ? ` · ${done} graded just now` : ''}{loading ? ' · loading…' : ''}
          </span>
        </div>
  );

  return (
    /* A <main> like every other page has, so "skip to content" and a screen
       reader's landmark list reach the queue. Embedded, it is a panel inside
       an eval's page, which already has one — two would be a landmark that
       means nothing. */
    <Root className="flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {embedded ? (
        <div className="flex min-h-10 shrink-0 items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-1">
          <SegmentedTabs size="sm" ariaLabel="What to review" value={mode} onChange={setMode} options={[{ value: 'grade', label: 'Grading' }, { value: 'audit', label: 'Spot checks' }]} />
          {mode === 'grade' && toolbar}
        </div>
      ) : (
      <div className="flex h-8 shrink-0 items-center gap-1.5 border-b border-[color:var(--border-default)] px-4">
        <button type="button" onClick={onBack} className="text-[13px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Evals</button>
        <ChevronRight className="h-3 w-3 text-[color:var(--text-muted)]" />
        <span className="text-[13px] font-medium text-[color:var(--text-primary)]">Grading queue</span>
        <SegmentedTabs size="sm" ariaLabel="What to review" className="ml-2" value={mode} onChange={setMode} options={[{ value: 'grade', label: 'Grading' }, { value: 'audit', label: 'Spot checks' }]} />
        {filterName && <span className="text-[12px] text-[color:var(--text-secondary)]">· {filterName}</span>}
        <div className="flex-1" />
        {/* The picker counts whichever tab you are on. On Spot checks the
            grading totals are a different queue entirely — it read "Every
            eval (1068) · 972 waiting" above a list of one. */}
        <div className="w-[220px]">
          <BrandDropdown compact searchable searchPlaceholder="Search evals…" ariaLabel="Eval" value={lineageId || ''} onChange={(v) => onLineage(v || null)}
            options={[{ value: '', label: `Every eval (${pickerTotal})` }, ...pickerCounts.map((c) => ({ value: c.lineage_id, label: c.name, hint: `${c.open_items} ${mode === 'audit' ? 'to rule on' : 'waiting'}` }))]} />
        </div>
      </div>
      )}

      {mode === 'audit' ? <AuditDisagreementsView teamId={teamId} lineageId={lineageId} onOpen={setOverlay} /> : (
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!embedded && <div className="flex flex-col gap-2 bg-surface px-3 pb-2 pt-3 sm:px-4">{toolbar}</div>}

        {notice && (notice.bulk || notice.id !== openId) && (
          <div role={notice.error ? 'alert' : 'status'} data-testid="eval3-queue-notice" className={`flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-1.5 text-[12px] ${notice.error ? 'text-danger' : 'text-[color:var(--text-secondary)]'}`}>
            {notice.text}
            <button type="button" onClick={() => setNotice(null)} className="ml-auto rounded px-1.5 py-0.5 text-[11px] text-[color:var(--text-muted)] hover:bg-surface-hover">Dismiss</button>
          </div>
        )}
        {error && <div role="alert" className="mx-4 mt-3 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-2 text-[12px] text-danger">{error}</div>}

        {loading && !items.length && <div className="flex justify-center py-12"><BrandLoader label="Loading the queue" /></div>}

        {!loading && !items.length && !error && (
          <div className="mx-4 mb-4 mt-3 flex h-40 items-center justify-center rounded-md border border-dashed border-[color:var(--border-default)] text-[12px] text-[color:var(--text-muted)]">
            {done > 0 ? `All caught up — ${done} graded.` : `Nothing is waiting for a person${filterName ? ` on ${filterName}` : ''}.`}
          </div>
        )}

        {items.length > 0 && (
          <div className="mx-4 mb-4 mt-3 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] table-fixed text-left">
                <caption className="sr-only">Waiting for a person, longest first</caption>
                <thead>
                  <tr className={tableHeaderRowClass}>
                    <th scope="col" className={`${th} w-8`}>
                      <span className="sr-only">Select</span>
                      {selectable.length > 0 && (
                        <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select everything listed'}
                          title={allPicked ? 'Clear the selection' : 'Select everything listed'}
                          onChange={() => setPicked(allPicked ? new Set() : new Set(selectable.map((i) => i.id)))} />
                      )}
                    </th>
                    <th scope="col" className={`${th} w-[30%]`}>Asked</th>
                    <th scope="col" className={th}>Answered</th>
                    {showEvalColumn && <th scope="col" className={`${th} w-[130px]`}>Eval</th>}
                    <th scope="col" className={`${th} w-[56px] text-right`}>Waited</th>
                    {/* Pinned: with a panel open the list can be narrower than
                        the table, and an answer column that scrolls off is an
                        answer nobody can give without scrolling first. */}
                    <th scope="col" className={`${th} sticky right-0 bg-surface text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)]`} style={{ width: answerColumnWidth }}>Your answer</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((it) => {
                    const isOpen = it.id === openId;
                    const busy = grading?.id === it.id ? grading.choice : null;
                    const ask = it.ask || shortId(unitIdOf(it)) || '—';
                    return (
                      <tr key={it.id} onClick={() => setOpenId(it.id)} tabIndex={0} aria-selected={isOpen}
                        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpenId(it.id); } }}
                        aria-label={`${ask}, waiting ${ago(it.created_at)}`}
                        className={`group ${tableRowClass} ${isOpen ? 'bg-surface-hover' : ''} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]`}>
                        <td className={td} onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={picked.has(it.id)} disabled={!picked.has(it.id) && !canPick(it)} aria-label={`Select “${ask}” to answer with others`}
                            title={!picked.has(it.id) && picked.size && !canPick(it) ? `Answered differently (${kindLabel(it.options)})` : 'Select to answer several at once'}
                            onChange={() => setPicked((cur) => { const n = new Set(cur); if (n.has(it.id)) n.delete(it.id); else n.add(it.id); return n; })} />
                        </td>
                        <td className={`${td} truncate text-[color:var(--text-primary)]`} title={it.ask || undefined}>{ask}</td>
                        <td className={`${td} truncate text-[color:var(--text-secondary)]`} title={it.answer || undefined}>{it.answer || '—'}</td>
                        {showEvalColumn && <td className={`${td} truncate text-[color:var(--text-secondary)]`} title={it.lineage_name}>{it.lineage_name}</td>}
                        <td className={`${td} whitespace-nowrap text-right text-[color:var(--text-muted)]`} title={it.created_at}>{ago(it.created_at)}</td>
                        <td className={`${td} sticky right-0 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)] ${isOpen ? 'bg-surface-hover' : 'bg-surface group-hover:bg-surface-hover'}`} onClick={(e) => e.stopPropagation()}>
                          <AnswerControl options={it.options} routes={it.routes} bands={it.bands} busy={busy}
                            name={`Grade “${ask}”`} labelFor={(w) => `Grade “${ask}”: ${w}`} onGrade={(v) => grade(it.id, v)} />
                        </td>
                      </tr>
                    );
                  })}
                  {visible.length === 0 && (
                    <tr><td colSpan={showEvalColumn ? 6 : 5} className={`${td} py-6 text-center text-[color:var(--text-muted)]`}>{query ? `Nothing here matches “${query}”.` : 'Nothing here matches.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* The rest of the queue, on request. Under the list, because
                asking for it is what a reviewer does after reading this page,
                not before. */}
            <TableFooter
              shown={items.length}
              total={waiting}
              hasMore={items.length < waiting}
              loading={loadingMore}
              onLoadMore={loadMore}
              className="border-t border-[color:var(--border-default)] px-3 pb-2"
            />
          </div>
        )}

        {/* The selection: one bar floating over the list, so ticking more never moves the rows. */}
        {picked.size > 0 && (
          <div className="pointer-events-none sticky bottom-3 z-10 flex justify-center px-4">
            <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] px-3 py-2 shadow-token-lg" role="group" aria-label="Answer the selection">
              <span className="text-[12px] font-medium tabular-nums text-[color:var(--text-primary)]">{picked.size} selected</span>
              {sharedAnswer ? (
                <>
                  <span className="text-[11.5px] text-[color:var(--text-muted)]">answer all</span>
                  <span className={sharedAnswer.options.type === 'number' && !scaleValues(sharedAnswer.options).length ? 'w-[260px]' : ''}>
                    <AnswerControl place="bulk" options={sharedAnswer.options} routes={[]} bands={sharedAnswer.bands}
                      busy={grading?.bulk ? grading.choice : null} name={`Answer all ${picked.size}`}
                      labelFor={(w) => `Answer all ${picked.size} with ${w}`} onGrade={gradePicked} />
                  </span>
                </>
              ) : (
                <span className="text-[11.5px] text-[color:var(--text-muted)]">These steps do not ask the same question. Narrow the selection to one eval.</span>
              )}
              <button type="button" onClick={() => setPicked(new Set())} className="h-7 rounded-md px-2 text-[12px] text-[color:var(--text-muted)] hover:bg-surface-hover">Clear</button>
            </div>
          </div>
        )}
      </div>
      )}

      <SidePanel
        open={Boolean(openId) && mode === 'grade'}
        onClose={() => setOpenId(null)}
        closeOnEscape={!overlay}
        ariaLabel="Item to grade"
        storageKey="trodo.evals3.queuePanel.width"
        defaultWidth={520}
        title={(
          <span className="flex items-baseline gap-1.5">
            <span className="truncate">{detail?.item ? (embedded ? shortId(unitIdOf(detail.item)) || 'Item' : detail.item.lineage_name) : 'Item'}</span>
            {detail?.item?.version != null && <span className="shrink-0 text-[11px] font-normal text-[color:var(--text-muted)]">v{detail.item.version}</span>}
          </span>
        )}
        actions={target ? (
          <button type="button" onClick={openFull} className="inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded border border-[color:var(--border-default)] px-2 text-[11px] text-[color:var(--text-secondary)] hover:bg-surface-hover">
            <ExternalLink className="h-3 w-3" aria-hidden /> Open the full {target.kind}
          </button>
        ) : null}
      >
            <div className="min-h-0 flex-1 px-4 py-4">
              {!detail && <PageLoader label="Loading this item" />}
              {detail?.error && <div className="text-[12px] text-danger" role="alert">{detail.error}</div>}
              {detail?.item && (
                <div className="flex flex-col gap-4">
                  <div className="rounded-md border border-[color:var(--border-strong)] bg-[color:var(--surface-sunken)] px-3 py-2.5 text-[13.5px] leading-snug text-[color:var(--text-primary)]" data-testid="eval3-queue-question">
                    {detail.options?.instructions || 'Pick the option that fits.'}
                  </div>

                  {trace?.run && (
                    <>
                      <Block label="The user asked"><Said text={trace.run.ask ?? trace.run.input} raw={trace.run.input} /></Block>
                      <Block label="The agent answered"><Said text={trace.run.answer ?? trace.run.output} raw={trace.run.output} /></Block>
                    </>
                  )}
                  {trace?.span && (
                    <Block label={`The step: ${trace.span.kind || ''} ${trace.span.name || trace.span.tool_name || ''}`}>
                      <div className="flex flex-col gap-2">
                        <Said text={trace.span.ask ?? trace.span.input} raw={trace.span.input} />
                        <Said text={trace.span.answer ?? trace.span.output} raw={trace.span.output} />
                      </div>
                    </Block>
                  )}
                  {trace?.transcript && <Block label="The conversation"><div className={`${pre} max-h-80`}>{trace.transcript}</div></Block>}
                  {!trace && <div className="text-[11.5px] text-[color:var(--text-muted)]">The trace could not be summarised here. Open the full {target?.kind} to read it.</div>}

                  {detail.path?.length > 0 && (
                    <Block label="Already checked">
                      <ol className="flex flex-wrap items-center gap-1.5" data-testid="eval3-queue-path">
                        {detail.path.map((p) => {
                          const value = p.output?.effective_value ?? p.output?.value;
                          const shown = value && typeof value === 'object' ? Object.entries(value).map(([k, v]) => `${k} ${fmt(v)}`).join(', ') : fmt(value);
                          return (
                            <li key={p.seq} className="rounded-full border border-[color:var(--border-default)] px-2 py-0.5 text-[11px] text-[color:var(--text-secondary)]">
                              <span className="font-medium text-[color:var(--text-primary)]">{detail.node_names?.[p.node_id] || p.node_id}</span>: {p.error ? 'failed' : shown}
                            </li>
                          );
                        })}
                      </ol>
                    </Block>
                  )}

                  <Block label="Your answer">
                    {detail.options?.type === 'number' ? (
                      <PanelScale options={detail.options} bands={detail.bands} busy={grading?.id === detail.item.id ? grading.choice : null}
                        onGrade={(v) => grade(detail.item.id, v, { fromPanel: true })} />
                    ) : (detail.options?.values || []).length ? (
                      <div className="flex flex-wrap gap-2" role="group" aria-label="Options">
                        {detail.options.values.map((v, i) => {
                          const route = detail.routes?.find((r) => r.value === v);
                          const word = wordFor(detail.options, v);
                          const tone = toneOf(word);
                          return (
                            <button key={v} type="button" disabled={Boolean(grading)} onClick={() => grade(detail.item.id, v, { fromPanel: true })} aria-label={`Grade ${word}`}
                              className={`flex min-w-[120px] flex-col items-start rounded-lg border border-[color:var(--border-default)] px-3 py-2 text-left hover:bg-surface-hover disabled:opacity-50 ${TONE_HOVER[tone]}`}>
                              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--text-primary)]">
                                {i < 9 && <span className="rounded bg-surface-hover px-1 font-mono text-[10px] text-[color:var(--text-muted)]">{i + 1}</span>}
                                {grading?.id === detail.item.id && grading.choice === v ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <ToneIcon tone={tone} />}
                                {word}
                              </span>
                              {route && <span className="text-[10.5px] text-[color:var(--text-muted)]">then {route.label}</span>}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-[12px] text-danger">This step declares nothing for a person to decide in the version it ran under, so it cannot be graded here.</div>
                    )}
                  </Block>

                  {notice && notice.id === detail.item.id && (
                    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-[12px] ${notice.error ? 'border-[color:var(--feedback-error)] text-danger' : 'border-[color:var(--border-default)] text-[color:var(--text-secondary)]'}`} role="status" data-testid="eval3-queue-notice">
                      {notice.text}
                      {!notice.error && (
                        <button type="button" onClick={() => leave([detail.item.id], { advance: detail.item.id })} className="ml-auto rounded border border-[color:var(--border-default)] px-2 py-0.5 text-[11px] hover:bg-surface-hover">Next</button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
      </SidePanel>

      {overlay?.kind === 'run' && <div className="fixed inset-0 z-[220]"><AgentRunDetailPage teamId={teamId} runId={overlay.id} onClose={() => setOverlay(null)} /></div>}
      {overlay?.kind === 'span' && <div className="fixed inset-0 z-[220]"><TraceDetailPage teamId={teamId} spanId={overlay.id} onClose={() => setOverlay(null)} onRunClick={(id) => setOverlay({ kind: 'run', id })} onSpanClick={(id) => setOverlay({ kind: 'span', id })} /></div>}
      {overlay?.kind === 'conversation' && <div className="fixed inset-0 z-[220]"><ConversationDetailDrawer teamId={teamId} conversationId={overlay.id} onClose={() => setOverlay(null)} /></div>}
    </Root>
  );
}
